import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { AlertTriangle, Plus, Pencil, Printer } from "lucide-react"
import { formatBRDate, todayISODate } from "@/lib/br-dates"
import { useGranjaAdmin } from "@/hooks/use-granja-admin"
import { MortalidadeList } from "@/components/mortalidades/mortalidade-list"
import { API_BASE } from '@/lib/api-base'
import {
  type Mortalidade,
  type MortalidadeListResponse,
  formatMortalidadeResumo,
  mortalidadesQueryString,
} from "@/lib/mortalidades"

interface Classificacao {
  id: string
  tenant_id: string
  galpao_id: string
  lote_id: string | null
  coleta_id: string | null
  codigo_coleta: string | null
  data: string
  jumbo: number
  extra: number
  grande: number
  medio: number
  pequeno: number
  industrial: number
  trincado: number
  sujo: number
  total_classificado: number
  total_coletado: number | null
  criado_em: string
}

interface Galpao {
  id: string
  nome: string
  status: string
}

interface Lote {
  id: string
  codigo: string
  galpao_id: string
  status: string
}

interface ColetaPendente {
  id: string
  codigo_coleta: string
  galpao_id: string
  lote_id: string
  data: string
  hora: string
  total_ovos: number
  ovos_quebrados_na_coleta: number
  idade_minutos: number | null
  status_classificacao: string
}

const STATUS_CLASSIFICACAO_LABEL: Record<string, string> = {
  PENDENTE: "Aguardando classificação",
  EM_CLASSIFICACAO: "Em classificação",
  CLASSIFICADA: "Classificada",
}

function parseCount(raw: string): number {
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
}

function ClassificacoesPage() {
  const apiBase = API_BASE
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()
  const { canDecideMortalidade } = useGranjaAdmin()
  const [classificacoes, setClassificacoes] = useState<Classificacao[]>([])
  const [galpoes, setGalpoes] = useState<Galpao[]>([])
  const [lotes, setLotes] = useState<Lote[]>([])
  const [pendentes, setPendentes] = useState<ColetaPendente[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editClassificacao, setEditClassificacao] = useState<Classificacao | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [printError, setPrintError] = useState<string | null>(null)
  const [printingClassificacaoId, setPrintingClassificacaoId] = useState<string | null>(null)
  const [tab, setTab] = useState<'pendentes' | 'classificados'>('pendentes')
  const [contextMortalidades, setContextMortalidades] = useState<Mortalidade[]>([])
  const latestContextKeyRef = useRef<string | null>(null)
  const [todasMortalidades, setTodasMortalidades] = useState<Mortalidade[]>([])
  const [form, setForm] = useState({
    galpao_id: "",
    lote_id: "",
    coleta_id: "",
    data: todayISODate(),
    jumbo: 0,
    extra: 0,
    grande: 0,
    medio: 0,
    pequeno: 0,
    industrial: 0,
    trincado: 0,
    sujo: 0,
  })

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    fetchClassificacoes()
    fetchGalpoes()
    fetchLotes()
    fetchPendentes()
    fetchMortalidadesColeta()
  }, [user, isSuperAdmin, selectedTenantId])

  const fetchMortalidadesColeta = async () => {
    try {
      const qs = mortalidadesQueryString({ limit: 100 })
      const res = await apiFetch(`${apiBase}/api/mortalidades?${qs}`)
      if (res.ok) {
        const data = (await res.json()) as MortalidadeListResponse
        setTodasMortalidades(data.items)
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }

  const fetchContextMortalidades = useCallback(async () => {
    if (editClassificacao) return
    let qs: string | null = null
    if (form.coleta_id) {
      qs = mortalidadesQueryString({ coleta_id: form.coleta_id })
    } else if (form.galpao_id && form.lote_id && form.data) {
      qs = mortalidadesQueryString({
        galpao_id: form.galpao_id,
        lote_id: form.lote_id,
        data: form.data,
      })
    }
    if (!qs) {
      setContextMortalidades([])
      return
    }
    const requestKey = qs
    latestContextKeyRef.current = requestKey
    try {
      const res = await apiFetch(`${apiBase}/api/mortalidades?${qs}`)
      if (res.ok) {
        const data = (await res.json()) as MortalidadeListResponse
        if (latestContextKeyRef.current === requestKey) setContextMortalidades(data.items)
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }, [apiBase, apiFetch, editClassificacao, form.coleta_id, form.galpao_id, form.lote_id, form.data])

  useEffect(() => {
    if (!showForm || editClassificacao) {
      setContextMortalidades([])
      return
    }
    fetchContextMortalidades()
  }, [showForm, editClassificacao, fetchContextMortalidades])

  const fetchClassificacoes = async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/classificacoes/`)
      if (res.ok) {
        const data = await res.json()
        setClassificacoes(data.items || [])
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }

  const fetchGalpoes = async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/galpoes/`)
      if (res.ok) {
        setGalpoes((await res.json()).filter((g: Galpao) => g.status === "ativo"))
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }

  const fetchLotes = async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/lotes/`)
      if (res.ok) {
        setLotes((await res.json()).filter((l: Lote) => l.status === "ativo"))
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }

  const fetchPendentes = async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/coletas/pendentes`)
      if (res.ok) {
        setPendentes(await res.json())
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }

  const retryLoad = () => {
    setLoadError(null)
    void fetchClassificacoes()
    void fetchGalpoes()
    void fetchLotes()
    void fetchPendentes()
    void fetchMortalidadesColeta()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.galpao_id) {
      setSubmitError("Selecione o galpao.")
      return
    }
    setSubmitError(null)
    const payload = {
      galpao_id: form.galpao_id,
      lote_id: form.lote_id || null,
      coleta_id: form.coleta_id || null,
      data: form.data,
      jumbo: Math.max(0, Number(form.jumbo) || 0),
      extra: Math.max(0, Number(form.extra) || 0),
      grande: Math.max(0, Number(form.grande) || 0),
      medio: Math.max(0, Number(form.medio) || 0),
      pequeno: Math.max(0, Number(form.pequeno) || 0),
      industrial: Math.max(0, Number(form.industrial) || 0),
      trincado: Math.max(0, Number(form.trincado) || 0),
      sujo: Math.max(0, Number(form.sujo) || 0),
    }
    try {
      const res = await apiFetch(`${apiBase}/api/classificacoes/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        setShowForm(false)
        setForm({
          galpao_id: galpoes[0]?.id || "",
          lote_id: "",
          coleta_id: "",
          data: todayISODate(),
          jumbo: 0,
          extra: 0,
          grande: 0,
          medio: 0,
          pequeno: 0,
          industrial: 0,
          trincado: 0,
          sujo: 0,
        })
        fetchClassificacoes()
        fetchPendentes()
        fetchMortalidadesColeta()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        setSubmitError(typeof errData?.detail === "string" ? errData.detail : "Erro ao salvar.")
      }
    } catch (e) {
      console.error(e)
      setSubmitError("Erro de conexao.")
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editClassificacao) return
    setSubmitError(null)
    const payload = {
      jumbo: Math.max(0, Number(form.jumbo) || 0),
      extra: Math.max(0, Number(form.extra) || 0),
      grande: Math.max(0, Number(form.grande) || 0),
      medio: Math.max(0, Number(form.medio) || 0),
      pequeno: Math.max(0, Number(form.pequeno) || 0),
      industrial: Math.max(0, Number(form.industrial) || 0),
      trincado: Math.max(0, Number(form.trincado) || 0),
      sujo: Math.max(0, Number(form.sujo) || 0),
    }
    try {
      const res = await apiFetch(`${apiBase}/api/classificacoes/${editClassificacao.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        setEditClassificacao(null)
        fetchClassificacoes()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        setSubmitError(typeof errData?.detail === "string" ? errData.detail : "Erro ao salvar.")
      }
    } catch (e) {
      console.error(e)
      setSubmitError("Erro de conexao.")
    }
  }

  const getGalpaoNome = (id: string) => galpoes.find(g => g.id === id)?.nome || "N/A"
  const getLoteCodigo = (id: string | null) => id ? (lotes.find(l => l.id === id)?.codigo || "N/A") : "-"

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  const totalForm = (form.jumbo || 0) + (form.extra || 0) + (form.grande || 0) + 
    (form.medio || 0) + (form.pequeno || 0) + (form.industrial || 0) + 
    (form.trincado || 0) + (form.sujo || 0)

  const lotesDoGalpao = form.galpao_id
    ? lotes.filter(l => l.galpao_id === form.galpao_id)
    : lotes

  const pendentesDoGalpao = form.galpao_id
    ? pendentes.filter(p => p.galpao_id === form.galpao_id)
    : pendentes

  const mortalidadesPorColeta = (coletaId: string) =>
    todasMortalidades.filter((m) => m.coleta_id === coletaId)

  const selectedColeta = pendentes.find(p => p.id === form.coleta_id)
  const coletaStatusLabel = selectedColeta
    ? STATUS_CLASSIFICACAO_LABEL[selectedColeta.status_classificacao] ?? selectedColeta.status_classificacao
    : null

  const refreshMortalidades = () => {
    fetchContextMortalidades()
    fetchMortalidadesColeta()
  }

  const handleClassificarPendente = (p: ColetaPendente) => {
    setSubmitError(null)
    setEditClassificacao(null)
    setForm({
      galpao_id: p.galpao_id,
      lote_id: p.lote_id || "",
      coleta_id: p.id,
      data: p.data,
      jumbo: 0,
      extra: 0,
      grande: 0,
      medio: 0,
      pequeno: 0,
      industrial: 0,
      trincado: 0,
      sujo: 0,
    })
    setShowForm(true)
  }

  const handleImprimirEtiquetaClassificacao = async (classificacaoId: string) => {
    setPrintError(null)
    setPrintingClassificacaoId(classificacaoId)
    try {
      const res = await apiFetch(`${apiBase}/api/classificacoes/${classificacaoId}/etiqueta`)
      if (!res.ok) {
        throw new Error("Erro ao gerar PDF")
      }
      const contentType = res.headers.get("content-type") ?? ""
      if (!contentType.includes("pdf")) {
        throw new Error("Erro ao gerar PDF")
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      window.open(url, "_blank")
      setTimeout(() => URL.revokeObjectURL(url), 5000)
    } catch (e) {
      console.error(e)
      setPrintError("Nao foi possivel gerar a etiqueta. Tente novamente.")
    } finally {
      setPrintingClassificacaoId(null)
    }
  }

  const pendentesPreview = pendentes.slice(0, 5)

  if (isSuperAdmin && !selectedTenantId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Classificacoes de Ovos</h1>
        <Button onClick={() => {
          setSubmitError(null)
          setShowForm(true)
          setEditClassificacao(null)
          setForm({
            galpao_id: galpoes[0]?.id || "",
            lote_id: "",
            coleta_id: "",
            data: todayISODate(),
            jumbo: 0,
            extra: 0,
            grande: 0,
            medio: 0,
            pequeno: 0,
            industrial: 0,
            trincado: 0,
            sujo: 0,
          })
        }}>
          <Plus className="h-4 w-4 mr-2" /> Nova Classificacao
        </Button>
      </div>

      {loadError && (
        <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm flex items-center justify-between gap-2">
          <span>{loadError}</span>
          <Button type="button" variant="outline" size="sm" onClick={retryLoad}>
            Tentar novamente
          </Button>
        </div>
      )}

      {printError && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm flex items-center justify-between gap-2">
          <span>{printError}</span>
          <button type="button" onClick={() => setPrintError(null)} className="underline hover:no-underline">Fechar</button>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          variant={tab === 'pendentes' ? 'default' : 'ghost'}
          onClick={() => setTab('pendentes')}
        >
          Pendentes ({pendentes.length})
        </Button>
        <Button
          variant={tab === 'classificados' ? 'default' : 'ghost'}
          onClick={() => setTab('classificados')}
        >
          Ja Classificados ({classificacoes.length})
        </Button>
      </div>

      {tab === 'pendentes' && !showForm && !editClassificacao && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
          <CardHeader>
            <CardTitle className="text-amber-800 dark:text-amber-200 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {pendentes.length === 0
                ? 'Nenhuma coleta aguardando classificacao'
                : `${pendentes.length} coleta(s) aguardando classificacao`}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {pendentes.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma coleta pendente no momento.</p>
            ) : (
              <>
            {/* Mobile: cards empilhados — Classificar sempre visível e clicável sem scroll horizontal */}
            <div className="space-y-3 md:hidden">
              {pendentesPreview.map((p) => {
                const mortResumo = formatMortalidadeResumo(mortalidadesPorColeta(p.id))
                return (
                  <div
                    key={p.id}
                    className="rounded-lg border border-amber-200/80 bg-background p-4 space-y-3 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-mono text-sm font-semibold">{p.codigo_coleta}</p>
                        <p className="text-sm text-muted-foreground">{getGalpaoNome(p.galpao_id)}</p>
                      </div>
                      {mortResumo && (
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          {mortResumo}
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs">Para classificar</p>
                        <p className="font-semibold">{formatNumber(p.total_ovos)} ovos</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Trincados (info)</p>
                        <p className="font-medium">{formatNumber(p.ovos_quebrados_na_coleta || 0)}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      className="w-full"
                      onClick={() => handleClassificarPendente(p)}
                    >
                      Classificar
                    </Button>
                  </div>
                )
              })}
            </div>

            {/* Desktop: tabela sem coluna de ação dentro do scroll horizontal */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Codigo</th>
                    <th className="text-left py-2">Galpao</th>
                    <th className="text-right py-2">Para Classificar</th>
                    <th className="text-right py-2">Trincados (Info)</th>
                    <th className="text-right py-2">Mortalidade</th>
                    <th className="text-right py-2 sticky right-0 bg-amber-50 dark:bg-amber-950/20 py-2 pl-2">
                      Ação
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pendentesPreview.map((p) => {
                    const mortResumo = formatMortalidadeResumo(mortalidadesPorColeta(p.id))
                    return (
                    <tr key={p.id} className="border-b">
                      <td className="font-mono py-2">{p.codigo_coleta}</td>
                      <td className="py-2">{getGalpaoNome(p.galpao_id)}</td>
                      <td className="text-right py-2 font-medium">{formatNumber(p.total_ovos)}</td>
                      <td className="text-right py-2 text-muted-foreground">
                        {formatNumber(p.ovos_quebrados_na_coleta || 0)}
                      </td>
                      <td className="text-right py-2">
                        {mortResumo ? (
                          <Badge variant="secondary" className="text-xs">
                            {mortResumo}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="text-right py-2 sticky right-0 bg-amber-50 dark:bg-amber-950/20 pl-2">
                        <Button size="sm" type="button" onClick={() => handleClassificarPendente(p)}>
                          Classificar
                        </Button>
                      </td>
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={showForm || !!editClassificacao}
        onOpenChange={(open) => {
          if (!open) {
            setShowForm(false)
            setEditClassificacao(null)
            setSubmitError(null)
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">
            {editClassificacao ? 'Editar Classificacao' : 'Nova Classificacao'}
          </DialogTitle>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold pr-10">
              {editClassificacao ? 'Editar Classificacao' : 'Nova Classificacao'}
            </h2>
            {submitError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                {submitError}
              </div>
            )}
            <form onSubmit={editClassificacao ? handleUpdate : handleSubmit} className="space-y-4">
              {!editClassificacao && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Galpao</Label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                        value={form.galpao_id}
                        onChange={(e) => setForm({ ...form, galpao_id: e.target.value, lote_id: "", coleta_id: "" })}
                        required
                      >
                        <option value="">Selecione...</option>
                        {galpoes.map(g => (
                          <option key={g.id} value={g.id}>{g.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Plantel</Label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                        value={form.lote_id}
                        onChange={(e) => setForm({ ...form, lote_id: e.target.value })}
                      >
                        <option value="">Selecione...</option>
                        {lotesDoGalpao.map(l => (
                          <option key={l.id} value={l.id}>{l.codigo}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Coleta (opcional)</Label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                        value={form.coleta_id}
                        onChange={(e) => setForm({ ...form, coleta_id: e.target.value })}
                      >
                        <option value="">Sem coleta vinculada</option>
                        {pendentesDoGalpao.map(p => (
                          <option key={p.id} value={p.id}>{p.codigo_coleta} ({formatNumber(p.total_ovos)} ovos)</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Data</Label>
                    <Input
                      type="date"
                      value={form.data}
                      onChange={(e) => setForm({ ...form, data: e.target.value })}
                      required
                    />
                  </div>
                </>
              )}

              {selectedColeta && (
                <div className="p-3 rounded bg-muted text-sm space-y-1">
                  <div><strong>Coleta:</strong> {selectedColeta.codigo_coleta}</div>
                  <div><strong>Ovos para classificar:</strong> <span className="font-bold text-green-600">{formatNumber(selectedColeta.total_ovos)}</span></div>
                  {(selectedColeta.ovos_quebrados_na_coleta || 0) > 0 && (
                    <div className="text-muted-foreground">(na coleta foram identificados {formatNumber(selectedColeta.ovos_quebrados_na_coleta || 0)} trincados, ja descontados do total)</div>
                  )}
                </div>
              )}

              {(selectedColeta || (form.galpao_id && form.lote_id && form.data)) && !editClassificacao && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="rounded-lg border p-4 space-y-2">
                    <h3 className="text-sm font-semibold">Status da coleta (classificação)</h3>
                    {selectedColeta ? (
                      <>
                        <Badge variant="secondary">{coletaStatusLabel}</Badge>
                        <p className="text-xs text-muted-foreground">
                          Somente leitura — atualizado automaticamente pelo fluxo de classificação, não pela mortalidade.
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">Sem coleta vinculada neste lançamento.</p>
                    )}
                  </div>
                  <div className="rounded-lg border p-4 space-y-2">
                    <h3 className="text-sm font-semibold">Mortalidades no contexto</h3>
                    <MortalidadeList
                      items={contextMortalidades}
                      galpoes={galpoes}
                      lotes={lotes}
                      canDecide={canDecideMortalidade}
                      apiBase={apiBase}
                      apiFetch={apiFetch}
                      onChanged={refreshMortalidades}
                      compact
                      emptyMessage="Nenhuma mortalidade neste contexto."
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Jumbo</Label>
                  <Input type="number" min={0} value={form.jumbo || ""} placeholder="0" onChange={(e) => setForm({ ...form, jumbo: parseCount(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Extra</Label>
                  <Input type="number" min={0} value={form.extra || ""} placeholder="0" onChange={(e) => setForm({ ...form, extra: parseCount(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Grande</Label>
                  <Input type="number" min={0} value={form.grande || ""} placeholder="0" onChange={(e) => setForm({ ...form, grande: parseCount(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Medio</Label>
                  <Input type="number" min={0} value={form.medio || ""} placeholder="0" onChange={(e) => setForm({ ...form, medio: parseCount(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Pequeno</Label>
                  <Input type="number" min={0} value={form.pequeno || ""} placeholder="0" onChange={(e) => setForm({ ...form, pequeno: parseCount(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Industrial</Label>
                  <Input type="number" min={0} value={form.industrial || ""} placeholder="0" onChange={(e) => setForm({ ...form, industrial: parseCount(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Trincado (novos)</Label>
                  <Input type="number" min={0} value={form.trincado || ""} placeholder="0" onChange={(e) => setForm({ ...form, trincado: parseCount(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Sujos</Label>
                  <Input type="number" min={0} value={form.sujo || ""} placeholder="0" onChange={(e) => setForm({ ...form, sujo: parseCount(e.target.value) })} />
                </div>
              </div>

              {selectedColeta && (selectedColeta.ovos_quebrados_na_coleta || 0) > 0 && (
                <div className="p-3 rounded bg-blue-50 dark:bg-blue-950/20 border border-blue-200 text-sm">
                  <span className="text-blue-800 dark:text-blue-200">
                    <strong>Info:</strong> Na coleta foram identificados {formatNumber(selectedColeta.ovos_quebrados_na_coleta || 0)} ovos trincados. 
                    O campo "Trincado (novos)" acima e para informar trincados ADICIONAIS encontrados na classificacao.
                  </span>
                </div>
              )}

              <div className="p-3 rounded bg-muted/50">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total classificado:</span>
                  <span className="text-lg font-bold">{formatNumber(totalForm)} ovos</span>
                </div>
                {selectedColeta && (
                  <div className="mt-2 pt-2 border-t text-sm space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Ovos para classificar:</span>
                      <span className="font-medium">{formatNumber(selectedColeta.total_ovos)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Diferenca:</span>
                      <span className={totalForm > selectedColeta.total_ovos ? "text-red-600 font-medium" : "text-muted-foreground"}>
                        {formatNumber(selectedColeta.total_ovos - totalForm)} ovos
                      </span>
                    </div>
                    {totalForm > selectedColeta.total_ovos && (
                      <p className="text-red-600 text-xs">Total classificado maior que ovos disponiveis!</p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button type="submit">Salvar</Button>
                <Button variant="outline" type="button" onClick={() => { setShowForm(false); setEditClassificacao(null); setSubmitError(null) }}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {tab === 'classificados' && (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {classificacoes.length === 0 ? (
          <p className="text-muted-foreground col-span-full">Nenhuma classificacao registrada.</p>
        ) : null}
        {classificacoes.map((c) => (
          <Card key={c.id}>
            <CardHeader>
              <CardTitle className="flex justify-between items-center gap-2">
                <span className="text-base">
                  {c.codigo_coleta ? `Coleta ${c.codigo_coleta}` : formatBRDate(c.data)}
                </span>
                {c.total_coletado && c.total_classificado < c.total_coletado && (
                  <Badge variant="secondary">Parcial</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm">Galpao: {getGalpaoNome(c.galpao_id)}</p>
              <p className="text-muted-foreground text-sm">Plantel: {getLoteCodigo(c.lote_id)}</p>
              {c.total_coletado && (
                <p className="text-muted-foreground text-sm">
                  Coletado: {formatNumber(c.total_coletado)} | Classificado: {formatNumber(c.total_classificado)}
                </p>
              )}
              <div className="mt-2 grid grid-cols-4 gap-1 text-xs">
                {c.jumbo > 0 && <span className="bg-muted px-1 py-0.5 rounded">J: {formatNumber(c.jumbo)}</span>}
                {c.extra > 0 && <span className="bg-muted px-1 py-0.5 rounded">E: {formatNumber(c.extra)}</span>}
                {c.grande > 0 && <span className="bg-muted px-1 py-0.5 rounded">G: {formatNumber(c.grande)}</span>}
                {c.medio > 0 && <span className="bg-muted px-1 py-0.5 rounded">M: {formatNumber(c.medio)}</span>}
                {c.pequeno > 0 && <span className="bg-muted px-1 py-0.5 rounded">P: {formatNumber(c.pequeno)}</span>}
                {c.industrial > 0 && <span className="bg-muted px-1 py-0.5 rounded">I: {formatNumber(c.industrial)}</span>}
                {c.trincado > 0 && <span className="bg-muted px-1 py-0.5 rounded">T: {formatNumber(c.trincado)}</span>}
                {c.sujo > 0 && <span className="bg-muted px-1 py-0.5 rounded">S: {formatNumber(c.sujo)}</span>}
              </div>
              <p className="font-medium mt-2">Total: {formatNumber(c.total_classificado)} ovos</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditClassificacao(c)
                    setForm({
                      galpao_id: c.galpao_id,
                      lote_id: c.lote_id || "",
                      coleta_id: c.coleta_id || "",
                      data: c.data,
                      jumbo: c.jumbo,
                      extra: c.extra,
                      grande: c.grande,
                      medio: c.medio,
                      pequeno: c.pequeno,
                      industrial: c.industrial,
                      trincado: c.trincado,
                      sujo: c.sujo,
                    })
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" /> Editar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  title="Gerar PDF da etiqueta"
                  aria-label={
                    c.codigo_coleta
                      ? `Gerar etiqueta da classificacao da coleta ${c.codigo_coleta}`
                      : `Gerar etiqueta da classificacao ${c.id}`
                  }
                  disabled={printingClassificacaoId === c.id}
                  onClick={() => handleImprimirEtiquetaClassificacao(c.id)}
                >
                  <Printer className="h-4 w-4 mr-2" /> Imprimir etiqueta
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/classificacoes')({
  component: ClassificacoesPage,
})
